import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Compass, ArrowLeft, ShieldAlert, AlertCircle, RefreshCw, Loader2, Home } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useNow } from '@/lib/useNow';
import { useConsole } from '@/lib/data';
import { mapRow } from '@/lib/data';
import { ticketsApi } from '@/api/ticketsApi';
import { TicketDocument } from './console/TicketDocument';
import { ThemeToggle } from './theme-toggle';
import { AccountControl } from './console/AccountControl';
import { useTeamFields } from '@/lib/customFields';
import { TEAMS } from '@/lib/teams';
import type { Ticket } from '@/lib/mock';
import { ErrorBoundary } from './ErrorBoundary';

export function TicketDirectView() {
  const { ticketRef } = useParams<{ ticketRef: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, initialLoading, loading: authLoading, authState } = useAuth();
  const now = useNow(1000);

  const meName = user?.displayName || user?.email || 'User';
  const {
    tickets,
    setStatus,
    assign,
    addUpdate,
    relate,
    merge,
    addAttachment,
    removeAttachment,
    resolveTicket,
    setParent,
    requestApproval,
    decideApproval,
    updateField
  } = useConsole(meName);

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Authentication check: redirect to login ONLY when definitely unauthenticated
  useEffect(() => {
    if (authState === 'AUTH_UNAUTHENTICATED' && !initialLoading && !authLoading && !session) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`, {
        replace: true,
        state: { from: location }
      });
    }
  }, [authState, initialLoading, authLoading, session, navigate, location]);

  // Load ticket directly by ref/UUID
  const loadTicket = useCallback(async () => {
    if (!ticketRef || !session) return;
    setLoading(true);
    setErrorCode(null);
    setErrorMessage(null);

    try {
      const res = await ticketsApi.getTicketById(ticketRef);
      const raw = res?.ticket || res;
      if (!raw || !raw.id) {
        setErrorCode(404);
        setErrorMessage(`Ticket "${ticketRef}" not found.`);
        return;
      }
      const mapped = mapRow(raw);
      setTicket(mapped);
    } catch (err: any) {
      const status = err.response?.status || (err.message?.includes('403') ? 403 : err.message?.includes('404') ? 404 : 500);
      setErrorCode(status);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  }, [ticketRef, session]);

  useEffect(() => {
    if (session) {
      loadTicket();
    }
  }, [session, loadTicket]);

  const owner = ticket ? TEAMS.find((tm) => tm.name === ticket.team || tm.match.includes(ticket.team)) : null;
  const { fields: customFields } = useTeamFields(owner?.id || '');

  // Determine dashboard return path based on user role
  const role = (user?.role || '').toLowerCase().trim();
  const getDashboardPath = () => {
    if (role.includes('super admin') || role.includes('superadmin') || role === 'admin') return '/admin/dashboard';
    if (role === 'manager') return '/manager/dashboard';
    if (role === 'technician' || role.includes('technician') || role === 'agent' || role.includes('service desk')) return '/technician/dashboard';
    if (role === 'viewer' || role === 'auditor') return '/viewer/dashboard';
    return '/user/dashboard';
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(getDashboardPath());
    }
  };

  if (initialLoading || authLoading || (!session && !errorCode)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-theme transition-colors">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Authenticating session...</span>
        </div>
      </div>
    );
  }

  // 404 NOT FOUND STATE
  if (errorCode === 404) {
    return (
      <div className="flex min-h-screen flex-col bg-theme text-theme transition-colors">
        <header className="flex h-16 items-center justify-between border-b border-[var(--color-neo-border)] bg-surface px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl neo">
              <Compass className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">Compass Ticketing</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <AccountControl />
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--color-neo-border)] bg-surface p-8 shadow-xl text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500">
              <AlertCircle className="h-8 w-8" />
            </div>
            <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-500">
              404 NOT FOUND
            </span>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground">Ticket Not Found</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We could not find a ticket matching reference{' '}
              <span className="font-mono font-semibold text-foreground">{ticketRef}</span>.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {errorMessage || 'The ticket may have been deleted, moved, or the link in your email notification is incorrect.'}
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(getDashboardPath())}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow transition hover:opacity-95"
              >
                <Home className="h-4 w-4" />
                Return to Dashboard
              </button>
              <button
                onClick={loadTicket}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-neo-border)] bg-theme px-4 py-2.5 text-sm font-medium hover:bg-surface transition"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 403 ACCESS DENIED STATE
  if (errorCode === 403) {
    return (
      <div className="flex min-h-screen flex-col bg-theme text-theme transition-colors">
        <header className="flex h-16 items-center justify-between border-b border-[var(--color-neo-border)] bg-surface px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl neo">
              <Compass className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">Compass Ticketing</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <AccountControl />
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--color-neo-border)] bg-surface p-8 shadow-xl text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
              <ShieldAlert className="h-8 w-8" />
            </div>
            <span className="rounded-full bg-destructive/15 px-3 py-1 text-xs font-semibold text-destructive">
              403 ACCESS DENIED
            </span>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground">Access Restricted</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You do not have permission to view ticket{' '}
              <span className="font-mono font-semibold text-foreground">{ticketRef}</span>.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              In accordance with security and privacy policies, only the assigned support technician, service desk agents, administrators, and the original ticket requester may view this ticket.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(getDashboardPath())}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow transition hover:opacity-95"
              >
                <Home className="h-4 w-4" />
                Return to Dashboard
              </button>
              <button
                onClick={() => navigate('/login?reason=switch_account')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-neo-border)] bg-theme px-4 py-2.5 text-sm font-medium hover:bg-surface transition"
              >
                Switch Account
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 401 SESSION EXPIRED STATE
  if (errorCode === 401) {
    return (
      <div className="flex min-h-screen flex-col bg-theme text-theme transition-colors">
        <header className="flex h-16 items-center justify-between border-b border-[var(--color-neo-border)] bg-surface px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl neo">
              <Compass className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">Compass Ticketing</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <AccountControl />
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--color-neo-border)] bg-surface p-8 shadow-xl text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500">
              <AlertCircle className="h-8 w-8" />
            </div>
            <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-500">
              SESSION EXPIRED
            </span>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground">Session Expired</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your session has expired. Please sign in again to view ticket{' '}
              <span className="font-mono font-semibold text-foreground">{ticketRef}</span>.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow transition hover:opacity-95"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate(getDashboardPath())}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-neo-border)] bg-theme px-4 py-2.5 text-sm font-medium hover:bg-surface transition"
              >
                <Home className="h-4 w-4" />
                Return to Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // GENERIC APPLICATION ERROR STATE (500, network, etc.)
  if (errorCode && !ticket) {
    return (
      <div className="flex min-h-screen flex-col bg-theme text-theme transition-colors">
        <header className="flex h-16 items-center justify-between border-b border-[var(--color-neo-border)] bg-surface px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl neo">
              <Compass className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">Compass Ticketing</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <AccountControl />
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--color-neo-border)] bg-surface p-8 shadow-xl text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
              <AlertCircle className="h-8 w-8" />
            </div>
            <span className="rounded-full bg-destructive/15 px-3 py-1 text-xs font-semibold text-destructive">
              ERROR {errorCode}
            </span>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground">Unable to Load Ticket</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {errorMessage || 'A problem occurred while communicating with the server. Please verify your connection.'}
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={loadTicket}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow transition hover:opacity-95"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
              <button
                onClick={() => navigate(getDashboardPath())}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-neo-border)] bg-theme px-4 py-2.5 text-sm font-medium hover:bg-surface transition"
              >
                <Home className="h-4 w-4" />
                Return to Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // LOADING STATE
  if (loading || !ticket) {
    return (
      <div className="flex min-h-screen flex-col bg-theme text-theme transition-colors">
        <header className="flex h-16 items-center justify-between border-b border-[var(--color-neo-border)] bg-surface px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl neo">
              <Compass className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">Compass Ticketing</span>
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading ticket details for {ticketRef}...</span>
          </div>
        </main>
      </div>
    );
  }

  // TICKET LOADED SUCCESSFULLY - RENDER TICKET VIEW
  return (
    <div className="flex min-h-screen flex-col bg-theme text-theme transition-colors">
      {/* Top Header */}
      <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-[var(--color-neo-border)] bg-surface px-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="flex h-9 w-9 items-center justify-center rounded-xl neo-interactive bg-theme text-muted-foreground hover:text-foreground transition"
            title="Back to Dashboard"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <Link to={getDashboardPath()} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
              <Compass className="h-4 w-4 text-primary" />
              <span>Compass</span>
            </Link>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-sm text-muted-foreground">Tickets</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-sm font-semibold text-foreground font-mono">{ticket.ref}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadTicket}
            className="flex h-9 w-9 items-center justify-center rounded-xl neo-interactive bg-theme text-muted-foreground hover:text-foreground transition"
            title="Refresh ticket"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <ThemeToggle />
          <AccountControl />
        </div>
      </header>

      {/* Main Ticket Document Surface */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6">
          <div className="rounded-2xl border border-[var(--color-neo-border)] bg-surface shadow-neo overflow-hidden">
            <ErrorBoundary>
              <TicketDocument
                ticket={ticket}
                now={now}
                allTickets={tickets}
                customFields={customFields}
                actions={{
                  onStatus: async (s: string) => {
                    await setStatus(ticket.id, s);
                    setTicket(prev => prev ? { ...prev, state: s } : null);
                  },
                  onAssign: async (a: string | null, name?: string) => {
                    await assign(ticket.id, a, name);
                    setTicket(prev => prev ? { ...prev, assigneeId: a, assignee: name || null } : null);
                  },
                  onUpdate: async (b: string, internal: boolean, emailData?: any) => {
                    await addUpdate(ticket.id, b, internal, emailData);
                    await loadTicket();
                  },
                  onRelate: (target: string) => relate(ticket.id, target),
                  onMerge: (into: string) => merge(ticket.id, into),
                  onAddFile: async (f: File) => {
                    await addAttachment(ticket.id, f);
                    await loadTicket();
                  },
                  onRemoveAttachment: async (attId: string) => {
                    await removeAttachment(ticket.id, attId);
                    await loadTicket();
                  },
                  onResolve: async (s: string, c: string, n: string) => {
                    await resolveTicket(ticket.id, s, c, n);
                    await loadTicket();
                  },
                  onSetParent: (parentId: string | null) => setParent(ticket.id, parentId),
                  onUpdateField: (field: string, value: any) => {
                    updateField(ticket.id, field, value);
                    setTicket(prev => prev ? { ...prev, [field]: value } : null);
                  },
                  onRequestApproval: (approver: string) => requestApproval(ticket.id, approver),
                  onDecideApproval: (appId: string, dec: 'approved' | 'rejected', note?: string) => decideApproval(ticket.id, appId, dec, note),
                }}
              />
            </ErrorBoundary>
          </div>
        </div>
      </main>
    </div>
  );
}
